import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../infrastructure/api/api";
import CharacterCompanion from "../../components/characterCompanion/CharacterCompanion";
import { ArrowLeft, Target, Sparkles, Clock, LayoutGrid } from 'lucide-react';
import "./updatedSortingPage.css";

export default function UpdatedSortingPage({ isEmbedded, embeddedId, onBack, onComplete, applyResilienceChange }) {
    const { t } = useTranslation();
    const params = useParams();
    const id = isEmbedded ? embeddedId : params.id;
    const navigate = useNavigate();
    
    const [scenario, setScenario] = useState(null);
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [boxes, setBoxes] = useState([]);
    const [draggedItem, setDraggedItem] = useState(null);
    const [sortedCount, setSortedCount] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [highlightedBox, setHighlightedBox] = useState(null);
    const [showCompletionMenu, setShowCompletionMenu] = useState(false);
    const [score, setScore] = useState(100);
    const [sessionTime, setSessionTime] = useState(0);
    const sessionStartTime = useRef(Date.now());

    const handleClose = () => {
        if (isEmbedded && onBack) {
            onBack();
        } else {
            navigate(-1);
        }
    };

    useEffect(() => {
        const timer = setInterval(() => {
            setSessionTime(Math.floor((Date.now() - sessionStartTime.current) / 1000));
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const loadScenario = async () => {
            try {
                let data = null;
                if (id && id !== 'null' && !id.startsWith('hardcoded-sorting')) {
                    data = await api.getScenarioById(id).catch(e => null);
                }

                setScenario(data && !data.message ? data : { name: t('sorting.fallback_title') });
                
                let dbBoxes = data && !data.message ? (data.content?.categories || data.categories || []) : [];
                let dbItems = data && !data.message ? (data.content?.items || data.items || []) : [];

                if (dbBoxes.length === 0 || dbItems.length === 0) {
                    console.log('Using hardcoded sorting data as fallback');
                    
                    if (id === 'hardcoded-sorting-2') {
                        setScenario({ name: t('sorting.circle_control') });
                        dbBoxes = [
                            { id: 'in_control', name: t('sorting.my_control'), color: '#8b5cf6' },
                            { id: 'out_control', name: t('sorting.out_control'), color: '#64748b' }
                        ];
                        dbItems = [
                            { id: 'i1', text: t('sorting.my_actions'), categoryId: 'in_control' },
                            { id: 'i2', text: t('sorting.others_actions'), categoryId: 'out_control' },
                            { id: 'i3', text: t('sorting.my_words'), categoryId: 'in_control' },
                            { id: 'i4', text: t('sorting.weather'), categoryId: 'out_control' },
                            { id: 'i5', text: t('sorting.others_thoughts'), categoryId: 'out_control' },
                            { id: 'i6', text: t('sorting.my_goals'), categoryId: 'in_control' },
                        ];
                    } else if (id === 'hardcoded-sorting-3') {
                        setScenario({ name: t('sorting.habits') });
                        dbBoxes = [
                            { id: 'healthy', name: t('sorting.healthy_habits'), color: '#10b981' },
                            { id: 'toxic', name: t('sorting.toxic_patterns'), color: '#f59e0b' }
                        ];
                        dbItems = [
                            { id: 'i1', text: t('sorting.regular_rest'), categoryId: 'healthy' },
                            { id: 'i2', text: t('sorting.comparing_others'), categoryId: 'toxic' },
                            { id: 'i3', text: t('sorting.saying_no'), categoryId: 'healthy' },
                            { id: 'i4', text: t('sorting.ignoring_emotions'), categoryId: 'toxic' },
                            { id: 'i5', text: t('sorting.perfectionism'), categoryId: 'toxic' },
                            { id: 'i6', text: t('sorting.physical_activity'), categoryId: 'healthy' },
                        ];
                    } else {
                        setScenario({ name: t('sorting.sorting_thoughts') });
                        dbBoxes = [
                            { id: 'positive', name: t('sorting.useful_thoughts'), color: '#10b981' },
                            { id: 'negative', name: t('sorting.destructive_thoughts'), color: '#f43f5e' }
                        ];
                        dbItems = [
                            { id: 'i1', text: t('sorting.overcome_this'), categoryId: 'positive' },
                            { id: 'i2', text: t('sorting.everything_wrong'), categoryId: 'negative' },
                            { id: 'i4', text: t('sorting.opportunity'), categoryId: 'positive' },
                            { id: 'i5', text: t('sorting.never_learn'), categoryId: 'negative' },
                            { id: 'i7', text: t('sorting.mistakes_help'), categoryId: 'positive' },
                            { id: 'i8', text: t('sorting.nobody_understands'), categoryId: 'negative' },
                        ];
                    }
                }

                setBoxes(dbBoxes.map(b => ({ ...b, items: [], isHighlighted: false })));
                setItems(dbItems.map((item, index) => ({
                    ...item,
                    id: item.id !== undefined ? item.id : index,
                    categoryId: item.categoryId !== undefined ? item.categoryId : item.category,
                    scale: 1,
                    isAbsolute: false
                })).sort(() => Math.random() - 0.5));
                setTotalItems(dbItems.length);
            } catch (err) {
                console.error('Error loading sorting scenario:', err);
            } finally {
                setLoading(false);
            }
        };

        loadScenario();
    }, [id]);

    const dragCloneRef = useRef(null);

    const handleDragStart = (e, item) => {
        // Create opaque clone for custom drag ghost
        const rect = e.target.getBoundingClientRect();
        
        const clone = e.target.cloneNode(true);
        clone.style.position = "absolute";
        clone.style.top = `${rect.top + window.scrollY}px`;
        clone.style.left = `${rect.left + window.scrollX}px`;
        clone.style.width = `${rect.width}px`;
        clone.style.height = `${rect.height}px`;
        clone.style.opacity = "1";
        clone.style.transform = "scale(1.05)";
        clone.style.boxShadow = "0 30px 60px -15px rgba(0, 0, 0, 0.8)";
        clone.style.zIndex = "-9999";
        clone.style.pointerEvents = "none";
        clone.classList.remove('hidden-source');
        document.body.appendChild(clone);
        dragCloneRef.current = clone;

        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;
        
        if (e.dataTransfer && e.dataTransfer.setDragImage) {
            e.dataTransfer.setDragImage(clone, offsetX, offsetY);
        }

        // Delay state update so browser can take the snapshot of the original element first
        // And then React will re-render and apply 'hidden-source' class to the original element
        setTimeout(() => {
            setDraggedItem(item);
        }, 0);
    };

    const handleDragEnd = (e) => {
        setDraggedItem(null);
        if (dragCloneRef.current && document.body.contains(dragCloneRef.current)) {
            document.body.removeChild(dragCloneRef.current);
            dragCloneRef.current = null;
        }
    };

    const handleBoxDrop = (e, boxId) => {
        e.preventDefault();
        e.stopPropagation(); // prevent area drop
        e.currentTarget.classList.remove('drag-over');
        if (!draggedItem) return;

        if (draggedItem.categoryId === boxId) {
            setBoxes(prev => prev.map(b => 
                b.id === boxId ? { ...b, items: [...b.items, draggedItem], isHighlighted: true } : b
            ));
            setItems(prev => prev.filter(item => item.id !== draggedItem.id));
            setSortedCount(prev => prev + 1);
            
            setTimeout(() => {
                setBoxes(prev => prev.map(b => 
                    b.id === boxId ? { ...b, isHighlighted: false } : b
                ));
            }, 500);

            if (sortedCount + 1 >= totalItems) {
                if (showCompletionMenu) return; 

                const finishExercise = async () => {
                    try {
                        if (id) {
                            await api.completeScenario(id, 100);
                        }
                    } catch(e) { console.error(e); }

                    if (applyResilienceChange) {
                        applyResilienceChange("exercise_complete", { score, name: scenario?.name || "Сортування" });
                    } else {
                        const userId = localStorage.getItem("userId");
                        if (userId) {
                            try {
                                await api.updateResilience(userId, "exercise_complete", { score }, scenario?.name || "Сортування");
                            } catch(e) {}
                        }
                    }
                    
                    if (onComplete) {
                        onComplete(id);
                    }
                    setShowCompletionMenu(true);
                };
                finishExercise();
            }
        } else {
            setHighlightedBox(boxId);
            setScore(prev => prev - 10);
            setTimeout(() => setHighlightedBox(null), 500);
        }
    };

    const handleAreaDrop = (e) => {
        e.preventDefault();
        if (!draggedItem) return;

        const rect = e.currentTarget.getBoundingClientRect();
        // center the item around the drop point (assuming width ~200, height ~60)
        const x = e.clientX - rect.left - 100;
        const y = e.clientY - rect.top - 30;

        setItems(prev => prev.map(item => 
            item.id === draggedItem.id ? { ...item, x, y, isAbsolute: true } : item
        ));
        setDraggedItem(null);
    };

    if (loading) return (
        <div className="dr-updated-sorting-layout items-center justify-center">
            <div className="w-12 h-12 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
        </div>
    );

    return (
        <div className="dr-updated-sorting-layout">
            <header className="dr-sorting-header">
                <div className="dr-header-content">
                    <button onClick={() => navigate('/exercises')} className="dr-back-btn">
                        <ArrowLeft /> {t('sorting.back_to_exercises')}
                    </button>
                    
                    {scenario && <h2 className="dr-sorting-title">{scenario.name}</h2>}
                    


                    <div className="dr-header-stats">
                        <div className="dr-stat-item">
                            <Target size={16} />
                            <span>{sortedCount}/{totalItems}</span>
                        </div>
                        <div className="dr-stat-item">
                            <Sparkles size={16} />
                            <span>{score}</span>
                        </div>
                    </div>
                </div>
            </header>

            <main className="dr-game-main">
                <div 
                    className="dr-sorting-area" 
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleAreaDrop}
                >
                    {items.map((item) => (
                        <div
                            key={item.id}
                            className={`dr-sorting-item ${draggedItem?.id === item.id ? 'hidden-source' : ''}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, item)}
                            onDragEnd={handleDragEnd}
                            style={{
                                ...(item.isAbsolute ? {
                                    position: 'absolute',
                                    left: `${item.x}px`,
                                    top: `${item.y}px`,
                                    zIndex: 10
                                } : { position: 'relative' })
                            }}
                        >
                            {item.text}
                        </div>
                    ))}
                    {items.length === 0 && sortedCount < totalItems && (
                        <div className="flex items-center justify-center h-full opacity-20 italic">
                            {t('sorting.all_items_placed')}
                        </div>
                    )}
                </div>

                <div className="dr-sorting-dock">
                    {boxes.map((box) => (
                        <div
                            key={box.id}
                            className={`dr-sorting-box ${highlightedBox === box.id ? 'highlight-wrong' : ''} ${box.isHighlighted ? 'highlight-correct' : ''}`}
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
                            onDragLeave={(e) => e.currentTarget.classList.remove('drag-over')}
                            onDrop={(e) => handleBoxDrop(e, box.id)}
                        >
                            <div className="dr-box-header">
                                <div className="dr-box-label" style={{ backgroundColor: box.color }}>
                                    {box.name}
                                </div>
                                <div className="dr-box-count">
                                    {box.items.length}
                                </div>
                            </div>
                            <div className="dr-box-content">
                                {box.items.map((_, idx) => (
                                    <div key={idx} className="dr-sorted-mini-item" style={{ backgroundColor: box.color }} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </main>

            {showCompletionMenu && (
                <div className="dr-completion-overlay">
                    <div className="dr-completion-card">
                        <div className="dr-completion-icon">🏆</div>
                        <h2>{t('sorting.success_message')}</h2>
                        <div className="dr-stats-container">
                            <div className="dr-stat-row">
                                <span>{t('sorting.score')}:</span>
                                <span>{score}</span>
                            </div>
                        </div>
                        <button className="dr-btn-primary" onClick={() => window.location.reload()}>{t('sorting.try_again')}</button>
                        <button className="dr-btn-secondary" onClick={() => navigate('/exercises')}>{t('sorting.close')}</button>
                    </div>
                </div>
            )}
            {}
        </div>
    );
}
